import {useCallback, useState} from 'react';
import {blockRangeToDocPositions} from '../../../helpers/blockRangeToDocPositions';

/**
 * Hook to handle replacing text for a compliance check task.
 * Phase 6 replacement — updates task status via REST instead of Firestore.
 */
export function useReplaceTaskText({view, task, marker}) {
    const [isReplacing, setIsReplacing] = useState(false);

    const handleReplaceText = useCallback(async () => {
        if (!view || !task?.blockId || !task?.range || !task?.suggestedText || !marker?.uid) {
            return;
        }
        const flagged = typeof task.originalText === 'string' ? task.originalText : '';
        if (flagged) {
            let blockText = null;
            view.state.doc.descendants((node) => {
                if (blockText != null) return false;
                if (node.attrs?.blockId === task.blockId) {
                    blockText = node.textContent || '';
                    return false;
                }
                return true;
            });
            if (blockText == null || !blockText.includes(flagged)) {
                setIsReplacing(false);
                return;
            }
        }

        setIsReplacing(true);
        try {
            const {state, dispatch} = view;
            const taskMarkType = state.schema.marks.task;

            if (!taskMarkType) {
                console.error('Task mark type not found in schema');
                setIsReplacing(false);
                return;
            }

            const [blockStart, blockEnd] = task.range;
            const docRange = blockRangeToDocPositions(view, task.blockId, blockStart, blockEnd);

            if (!docRange) {
                console.error('Could not find document positions for blockId:', task.blockId);
                setIsReplacing(false);
                return;
            }

            const {from, to} = docRange;
            let tr = state.tr;

            state.doc.nodesBetween(from, to, (node, pos) => {
                if (!node.isText) return true;
                for (const mark of node.marks) {
                    if (mark.type === taskMarkType && mark.attrs.uid === marker.uid) {
                        const markFrom = Math.max(from, pos);
                        const markTo = Math.min(to, pos + node.nodeSize);
                        tr = tr.removeMark(markFrom, markTo, mark);
                    }
                }
                return true;
            });

            tr = tr.replaceWith(from, to, state.schema.text(task.suggestedText));
            dispatch(tr);
            // Leave the task `suggested`. The Tools list hides it while the flagged
            // text is gone and shows it again when Ctrl+Z restores that text.
        } catch (error) {
            console.error('Error replacing text:', error);
        } finally {
            setIsReplacing(false);
        }
    }, [view, task, marker?.uid]);

    return {handleReplaceText, isReplacing};
}
